import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSearch, Library, Paperclip, Send, Smile, Sparkles, X } from 'lucide-react';
import { formatBytes } from '../../api';
import type { ContactConversation, ContactMessage } from '../../app/legacy-types';
import { contactCenterApi, contactCenterKeys, type ContactConversationFile } from './contactCenterApi';
import { friendlyDeliveryError } from './contactCenterModel';
import { SafeLibraryMedia } from './SafeContactMedia';

const COMMON_EMOJI = ['👍', '🔥', '😊', '🙏', '✅', '🎉', '👀', '😎', '🤝', '❤️', '😂', '👏', '🤔', '😢', '😡', '🚀'];

type SendMessage = (conversationId: string, body: string, files: File[]) => Promise<ContactMessage>;
type SendLibraryAsset = (conversationId: string, assetId: string) => Promise<ContactMessage>;

function authenticatedFileUrl(item: ContactConversationFile): string {
  const value = String(item.downloadUrl || '');
  if (value.startsWith('/api/v1/')) return value;
  if (value.startsWith('/api/')) return value.replace(/^\/api\//, '/api/v1/');
  return `/api/v1/contact-center/messages/${encodeURIComponent(item.messageId)}/files/${encodeURIComponent(item.id)}?download=1`;
}

export function ReplyComposer(props: {
  conversation: ContactConversation;
  onSent: (message: ContactMessage) => void;
  sendMessage?: SendMessage;
  sendLibraryAsset?: SendLibraryAsset;
  loadResources?: boolean;
}) {
  const loadResources = props.loadResources !== false;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [panel, setPanel] = useState<'emoji' | 'templates' | 'files' | 'media' | null>(null);
  const [fileSearch, setFileSearch] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const sendMessage = props.sendMessage || contactCenterApi.sendMessage;
  const sendLibraryAsset = props.sendLibraryAsset || contactCenterApi.sendLibraryAsset;

  const templatesQuery = useQuery({
    queryKey: contactCenterKeys.templates(props.conversation.provider, props.conversation.id),
    queryFn: ({ signal }) => contactCenterApi.templates(props.conversation.provider, props.conversation.id, signal),
    enabled: loadResources && panel === 'templates',
  });
  const filesQuery = useQuery({
    queryKey: contactCenterKeys.files(props.conversation.id, fileSearch),
    queryFn: ({ signal }) => contactCenterApi.files(props.conversation.id, fileSearch, signal),
    enabled: loadResources && panel === 'files',
  });
  const mediaQuery = useQuery({
    queryKey: contactCenterKeys.media(props.conversation.provider),
    queryFn: ({ signal }) => contactCenterApi.mediaLibrary(props.conversation.provider, signal),
    enabled: loadResources && panel === 'media',
  });

  useEffect(() => {
    setBody('');
    setFiles([]);
    setPanel(null);
    setError('');
    setFileSearch('');
  }, [props.conversation.id]);

  function addFiles(selection: FileList | File[] | null) {
    const incoming = Array.from(selection || []);
    if (!incoming.length) return;
    const oversized = incoming.filter(file => file.size > 100 * 1024 * 1024);
    const empty = incoming.filter(file => file.size <= 0);
    if (oversized.length) setError(`Файл превышает лимит 100 МБ: ${oversized.map(file => file.name).join(', ')}`);
    else if (empty.length) setError(`Пустой файл нельзя отправить: ${empty.map(file => file.name).join(', ')}`);
    const allowed = incoming.filter(file => file.size > 0 && file.size <= 100 * 1024 * 1024);
    setFiles(current => [...current, ...allowed]
      .filter((file, index, all) => all.findIndex(candidate => candidate.name === file.name && candidate.size === file.size && candidate.lastModified === file.lastModified) === index)
      .slice(0, 10));
  }

  async function submit() {
    if (pending || (!body.trim() && !files.length)) return;
    const submittedBody = body;
    const submittedFiles = [...files];
    setPending(true);
    setError('');
    try {
      const response = await sendMessage(props.conversation.id, submittedBody, submittedFiles);
      const delivery = response.payload?.delivery as { textDelivered?: boolean; textError?: string; failedFiles?: Array<{ name?: string; error?: string; index?: number }> } | undefined;
      const failed = Array.isArray(delivery?.failedFiles) ? delivery.failedFiles : [];
      const textFailed = Boolean(submittedBody.trim()) && delivery?.textDelivered === false;
      setBody(textFailed ? submittedBody : '');
      if (failed.length) {
        const indexes = new Set(failed.map(item => item.index).filter((value): value is number => Number.isInteger(value)));
        const names = new Set(failed.map(item => item.name).filter((value): value is string => Boolean(value)));
        setFiles(submittedFiles.filter((file, index) => indexes.size ? indexes.has(index) : names.has(file.name)));
      } else setFiles([]);
      if (textFailed || failed.length) {
        const parts = [
          ...(textFailed ? [`Текст не отправлен${delivery?.textError ? ` — ${delivery.textError}` : ''}`] : []),
          ...(failed.length ? [`Не отправлено файлов: ${failed.length}. ${friendlyDeliveryError(failed[0]?.error)}`] : []),
        ];
        setError(parts.join('. '));
      }
      props.onSent(response);
    } catch (reason) {
      setError(friendlyDeliveryError(reason instanceof Error ? reason.message : 'Не удалось отправить сообщение'));
    } finally {
      setPending(false);
    }
  }

  async function sendAsset(assetId: string) {
    setPending(true);
    setError('');
    try {
      const response = await sendLibraryAsset(props.conversation.id, assetId);
      props.onSent(response);
      setPanel(null);
    } catch (reason) {
      setError(friendlyDeliveryError(reason instanceof Error ? reason.message : 'Не удалось отправить стикер'));
    } finally {
      setPending(false);
    }
  }

  return <form className="contact-composer v3" onSubmit={event => { event.preventDefault(); void submit(); }}>
    {files.length > 0 && <div className="pending-files compact contact-pending-files">{files.map((file, index) => <span key={`${file.name}:${file.size}:${file.lastModified}`}><Paperclip /><b title={file.name}>{file.name}</b><small>{formatBytes(file.size)}</small><button type="button" aria-label={`Убрать файл ${file.name}`} onClick={() => setFiles(current => current.filter((_item, currentIndex) => currentIndex !== index))}><X /></button></span>)}</div>}
    {panel && <section className="contact-composer-panel" aria-label={panel === 'emoji' ? 'Выбор эмодзи' : panel === 'templates' ? 'Шаблоны ответов' : panel === 'files' ? 'Файлы беседы' : 'Эмодзи и стикеры'}>
      <header><b>{panel === 'emoji' ? 'Эмодзи' : panel === 'templates' ? 'Шаблоны ответов' : panel === 'files' ? 'Файлы беседы' : 'Эмодзи и стикеры'}</b><button type="button" aria-label="Закрыть панель" onClick={() => setPanel(null)}><X /></button></header>
      {panel === 'emoji' && <div className="contact-emoji-grid">{COMMON_EMOJI.map(emoji => <button type="button" key={emoji} aria-label={`Вставить ${emoji}`} onClick={() => { setBody(current => `${current}${emoji}`); setPanel(null); }}>{emoji}</button>)}</div>}
      {panel === 'templates' && <div className="contact-resource-list">{templatesQuery.isLoading && <p>Загружаем шаблоны…</p>}{templatesQuery.isError && <p className="form-error">Не удалось загрузить шаблоны</p>}{templatesQuery.data?.map(template => <button type="button" key={template.id} onClick={() => { setBody(template.preview); setPanel(null); }}><b>{template.name}</b><small>{template.preview}</small></button>)}{templatesQuery.data?.length === 0 && <p>Подходящих шаблонов пока нет.</p>}</div>}
      {panel === 'files' && <><label className="contact-resource-search"><FileSearch /><input value={fileSearch} onChange={event => setFileSearch(event.target.value)} placeholder="Поиск по названию файла" aria-label="Поиск файлов беседы" /></label><div className="contact-resource-list">{filesQuery.isLoading && <p>Ищем файлы…</p>}{filesQuery.isError && <p className="form-error">Не удалось загрузить файлы</p>}{filesQuery.data?.map(item => <a key={item.id} href={authenticatedFileUrl(item)}><Paperclip /><span><b>{item.name}</b><small>{item.mimeType} · {formatBytes(item.size)}</small></span></a>)}{filesQuery.data?.length === 0 && <p>Файлы не найдены.</p>}</div></>}
      {panel === 'media' && <div className="contact-media-grid">{mediaQuery.isLoading && <p>Загружаем набор…</p>}{mediaQuery.isError && <p className="form-error">Не удалось загрузить набор</p>}{mediaQuery.data?.map(asset => <button type="button" key={asset.id} aria-label={asset.asset_kind === 'EMOJI' ? `Вставить ${asset.title}` : `Отправить ${asset.title}`} onClick={() => asset.asset_kind === 'EMOJI' ? (setBody(current => `${current}${asset.emoji_text || ''}`), setPanel(null)) : void sendAsset(asset.id)} disabled={pending}><SafeLibraryMedia asset={asset} /><small>{asset.title}</small></button>)}{mediaQuery.data?.length === 0 && <p>Разрешённый набор пока пуст.</p>}</div>}
    </section>}
    <div className="contact-input-shell"><textarea aria-label="Текст ответа" value={body} onChange={event => setBody(event.target.value)} rows={4} placeholder={props.conversation.provider === 'EMAIL' ? 'Ответить на письмо…' : 'Написать ответ…'} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void submit(); } }} /><div className="contact-input-tools">
      <button className="icon-button" type="button" aria-label="Открыть эмодзи" onClick={() => setPanel(value => value === 'emoji' ? null : 'emoji')}><Smile /></button>
      <button className="icon-button" type="button" aria-label="Прикрепить файлы" onClick={() => fileInputRef.current?.click()}><Paperclip /></button>
      <input ref={fileInputRef} type="file" multiple hidden onChange={event => { addFiles(event.target.files); event.currentTarget.value = ''; }} />
      <button className="icon-button" type="button" aria-label="Открыть шаблоны ответов" onClick={() => setPanel(value => value === 'templates' ? null : 'templates')}><Library /></button>
      <button className="icon-button" type="button" aria-label="Найти файлы беседы" onClick={() => setPanel(value => value === 'files' ? null : 'files')}><FileSearch /></button>
      <button className="icon-button" type="button" aria-label="Открыть эмодзи и стикеры" onClick={() => setPanel(value => value === 'media' ? null : 'media')}><Sparkles /></button>
      <small>Enter — отправить, Shift+Enter — новая строка</small>
    </div></div>
    <button type="submit" className="button primary contact-send-button" aria-label="Отправить ответ" disabled={pending || (!body.trim() && !files.length)}><Send /> {pending ? 'Отправка…' : 'Отправить'}</button>
    {error && <div className="form-error contact-send-error" role="alert"><span>{error}</span><button type="button" aria-label="Закрыть уведомление" onClick={() => setError('')}><X /></button></div>}
  </form>;
}
